import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  TEMPLATE_LABELS,
  TEMPLATE_TYPES,
  TRACKED_FORM_LABELS,
  readTrackedFormMapping,
  trackedFormFieldNames,
  writeTrackedFormMapping,
} from '@leadgab/shared';
import type { TrackedFormLabel } from '@leadgab/shared';
import type { TemplateType } from '@leadgab/shared';
import { PageShell, Section } from '../components/PageShell.tsx';
import type { PageTab } from '../components/PageShell.tsx';
import { useConfirm } from '../components/ConfirmDialog.tsx';
import { Select } from '../components/Select.tsx';
import { Avatar, ChannelBadge, EmptyState, ErrorNotice, Spinner, formatRelative } from '../components/ui.tsx';
import { Modal } from '../components/Modal.tsx';
import {
  ArchiveIcon,
  ClipboardIcon,
  CodeIcon,
  EyeIcon,
  FileTextIcon,
  MegaphoneIcon,
  MessageSquareIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  PlugIcon,
  SettingsIcon,
  UserPlusIcon,
  UsersIcon,
  ZapIcon,
} from '../components/icons.tsx';
import { AutomationTab } from '../components/workspace/AutomationTab.tsx';
import { ChannelsTab } from '../components/workspace/ChannelsTab.tsx';
import { RetentionTab } from '../components/workspace/RetentionTab.tsx';
import { SettingsTab } from '../components/workspace/SettingsTab.tsx';
import { WidgetTab } from '../components/workspace/WidgetTab.tsx';
import type { SettingsDto } from '../components/workspace/shared.tsx';
import { ApiRequestError, api } from '../lib/api.ts';
import { useTabRoute } from '../lib/useTabRoute.ts';
import { useAuth } from '../lib/auth.tsx';
import { refreshSessions } from '../lib/sessionCache.ts';
import type { SessionDto, WireUserDto } from '../lib/api.ts';

const TABS = [
  'widget',
  'team',
  'conversations',
  'templates',
  'engagements',
  'forms',
  'automation',
  'channels',
  'retention',
  'settings',
] as const;
type Tab = (typeof TABS)[number];

/** The tabs that configure the workspace, and so need `/settings` loaded. */
const WORKSPACE_TABS: readonly Tab[] = ['widget', 'automation', 'channels', 'retention', 'settings'];

interface AgentRow {
  invitationUuid: string | null;
  userId: number | null;
  email: string;
  name: string;
  avatar: string | null;
  role: string;
  status: 'active' | 'pending';
}

interface ManagedSession extends SessionDto {
  assignedTo: WireUserDto | null;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export function ManagementPage(): React.JSX.Element {
  const [tab, setTab] = useTabRoute<Tab>('/management', TABS, TABS[0]);
  const { me } = useAuth();

  // Deleting a conversation is the owner's alone — see the route.
  const isOwner = me?.role === 'owner';

  // The workspace tabs read from it. Fetched here rather than inside each one
  // so the four of them share a single request under the same cache key the
  // Settings page uses.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingsDto>('/settings'),
  });

  return (
    <PageShell
      title="Management"
      description="Your team, every conversation, and the automated messages that go out."
      tabs={[
        {
          id: 'widget',
          label: 'Widget',
          icon: CodeIcon,
          help: {
            summary: 'The snippet that puts the chat bubble on your site, and what it asks visitors for.',
            points: [
              'Paste the snippet before </body> on every page you want the widget on.',
              'At least one of email or phone must stay on — with both off no visitor can start a chat.',
              'Turning the bubble off still lets you open the widget from your own buttons.',
            ],
          },
        },
        {
          id: 'team',
          label: 'Team',
          icon: UsersIcon,
          help: {
            summary:
              'Who can answer conversations in this workspace. Invited people set their own password from the link they receive.',
            points: [
              'Pending means the invitation has not been accepted — the account does not exist yet.',
              'Removing someone deletes their account and returns their conversations to Unassigned.',
              'Only owners and admins can invite or remove.',
            ],
          },
        },
        {
          id: 'conversations',
          label: 'Conversations',
          icon: MessageSquareIcon,
          help: {
            summary:
              'Every conversation in the workspace, including the archived and spam ones agents never see in Chats.',
            points: [
              'Read-only here — open a conversation from Chats to reply.',
              'Spam conversations are hidden from agents and are never pushed over the socket.',
            ],
          },
        },
        {
          id: 'templates',
          label: 'Templates',
          icon: FileTextIcon,
          help: {
            summary: 'Messages the system sends on your behalf, without an agent typing them.',
            points: [
              'Welcome goes out the moment a customer opens a new conversation.',
              'No Agents Online goes to a visitor in the widget when nobody is connected, at most once an hour per conversation. Other channels never see it — the person is not on the page to read it.',
              'Inventory alerts accept {{item}} — the item’s name — plus {{customerName}} and the key of any field you declared on the Inventory page.',
            ],
          },
        },
        {
          id: 'engagements',
          label: 'Engagements',
          icon: MegaphoneIcon,
          help: {
            summary:
              'Prompts the widget shows to visitors who have not started a conversation yet.',
            points: [
              'Only enabled ones are sent to the widget.',
              'A visitor sees at most one per page view, and none once they already have a conversation.',
            ],
          },
        },
        {
          id: 'forms',
          label: 'Tracked forms',
          icon: ClipboardIcon,
          help: {
            summary: "Forms on your own site whose submissions are copied into LeadGab.",
            points: [
              'Scan a page first to see which forms have named, visible fields.',
              'Capture is passive — your form keeps submitting exactly as it did before.',
            ],
          },
        },
        {
          id: 'automation',
          label: 'Automation',
          icon: ZapIcon,
          help: {
            summary: 'Replies that go out without an agent, and snippets agents can insert by hand.',
            points: [
              'Only the first matching auto response fires, so one message never triggers a burst.',
              'Preset replies appear under the composer in Chats.',
            ],
          },
        },
        ...(isOwner
          ? ([
              {
                id: 'channels',
                label: 'Channels',
                icon: PlugIcon,
                help: {
                  summary: 'Where conversations arrive from, besides the widget on your site.',
                  points: [
                    "Saving Twilio points the number's webhook here, which is also how the credentials get verified.",
                    'Mail sent to a configured address opens or continues a conversation.',
                    'A Facebook page can belong to one workspace only — inbound messages have to resolve to exactly one inbox.',
                  ],
                },
              },
              {
                id: 'retention',
                label: 'Retention',
                icon: ArchiveIcon,
                help: {
                  summary: 'How long quiet conversations stay in the queue, and who is blocked.',
                  points: [
                    'Archiving releases the assignee and returns the conversation to the pool.',
                    'Any new message revives an archived conversation.',
                    'Blocked customers are flagged as spam automatically on their next message.',
                  ],
                },
              },
              {
                id: 'settings',
                label: 'Settings',
                icon: SettingsIcon,
                help: {
                  summary: 'Workspace-wide switches that apply to everyone here.',
                  points: [
                    'Three switches, for the three emails this sends the team by itself.',
                    'Everything else it sends — an invitation, a password reset, a new account — is not a notification and cannot be turned off.',
                    'Turning any of them off changes nothing else: the conversation still opens, the submission is still captured.',
                  ],
                },
              },
            ] satisfies PageTab<Tab>[])
          : []),
      ]}
      active={tab}
      onChange={setTab}
    >
      {tab === 'team' ? <TeamTab /> : null}
      {tab === 'conversations' ? <ConversationsTab isOwner={isOwner} /> : null}
      {tab === 'templates' ? <TemplatesTab /> : null}
      {tab === 'engagements' ? <EngagementsTab /> : null}
      {tab === 'forms' ? <FormsTab /> : null}

      {/* The workspace tabs cannot render until their settings arrive. */}
      {settings.isLoading || !settings.data ? (
        WORKSPACE_TABS.includes(tab) ? <Spinner className="h-5 w-5 text-brand-600" /> : null
      ) : (
        <>
          {tab === 'widget' ? <WidgetTab settings={settings.data} isOwner={isOwner} /> : null}
          {tab === 'automation' ? <AutomationTab isOwner={isOwner} /> : null}
          {tab === 'channels' && isOwner ? <ChannelsTab settings={settings.data} /> : null}
          {tab === 'retention' && isOwner ? <RetentionTab settings={settings.data} /> : null}
          {tab === 'settings' && isOwner ? <SettingsTab settings={settings.data} /> : null}
        </>
      )}
    </PageShell>
  );
}

function TeamTab(): React.JSX.Element {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'agent'>('agent');
  const [error, setError] = useState<string | null>(null);

  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const agents = useQuery({
    queryKey: ['management', 'agents'],
    queryFn: () => api.get<{ items: AgentRow[] }>('/management/agents'),
  });

  const invite = useMutation({
    mutationFn: () => api.post<{ emailed: boolean; url?: string }>('/management/agents/invite', { email, role }),
    onSuccess: (result) => {
      setEmail('');
      setError(null);
      // Without SMTP configured the owner still needs the link to hand over.
      setInviteUrl(result.emailed ? null : (result.url ?? null));
      void queryClient.invalidateQueries({ queryKey: ['management', 'agents'] });
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiRequestError ? caught.message : 'Something went wrong');
    },
  });

  const remove = useMutation({
    mutationFn: (uuid: string) => api.delete(`/management/agents/${uuid}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['management', 'agents'] });
    },
  });

  return (
    <>
      <Section title="Invite a teammate" description="They will get a link to set their own password.">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            invite.mutate();
          }}
        >
          <div className="min-w-[16rem] flex-1">
            <label className="lg-label" htmlFor="invite-email">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              required
              className="lg-input"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </div>

          <div>
            <label className="lg-label" htmlFor="invite-role">
              Role
            </label>
            <Select<'admin' | 'agent'>
              id="invite-role"
              className="w-44"
              value={role}
              onChange={setRole}
              options={[
                { value: 'agent', label: 'Agent', description: 'Answers conversations' },
                { value: 'admin', label: 'Admin', description: 'Also manages the team' },
              ]}
            />
          </div>

          <button type="submit" className="lg-btn-primary" disabled={invite.isPending}>
            {invite.isPending ? <Spinner /> : <UserPlusIcon className="h-4 w-4" />}
            Send invite
          </button>
        </form>

        {error ? (
          <div className="mt-3">
            <ErrorNotice message={error} />
          </div>
        ) : null}

        {inviteUrl ? (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs break-all text-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
            Email is not configured on this server, so send them this link yourself: {inviteUrl}
          </p>
        ) : null}
      </Section>

      <Section title="Team">
        {agents.isLoading ? (
          <Spinner className="h-5 w-5 text-brand-600" />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {(agents.data?.items ?? []).map((agent) => (
              <li
                key={agent.invitationUuid ?? `owner-${String(agent.userId)}`}
                className="flex items-center gap-3 py-3"
              >
                <Avatar src={agent.avatar} name={agent.name || agent.email} size={36} />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-50">
                    {agent.name || agent.email}
                  </p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">{agent.email}</p>
                </div>

                <span className="lg-badge bg-slate-100 text-slate-600 capitalize dark:bg-slate-800 dark:text-slate-300">
                  {agent.role}
                </span>

                {agent.status === 'pending' ? (
                  <span className="lg-badge bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-200">
                    Pending
                  </span>
                ) : null}

                {/* The owner row has no invitation and cannot be removed here. */}
                {agent.invitationUuid ? (
                  <button
                    type="button"
                    className="lg-btn-ghost px-2 hover:text-rose-600"
                    title="Remove"
                    onClick={() => {
                      void confirm({
                        title: `Remove ${agent.name || agent.email}?`,
                        message:
                          'Their account is deleted and every conversation they hold returns to Unassigned.',
                        confirmLabel: 'Remove',
                        destructive: true,
                      }).then((ok) => {
                        if (ok) remove.mutate(agent.invitationUuid as string);
                      });
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span className="sr-only">Remove</span>
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}

function ConversationsTab({ isOwner }: { isOwner: boolean }): React.JSX.Element {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'archived' | 'spam'>('all');
  const [note, setNote] = useState<string | null>(null);

  const sessions = useQuery({
    queryKey: ['management', 'sessions', filter],
    queryFn: () =>
      api.get<{ items: ManagedSession[] }>('/management/sessions', {
        archived: filter === 'archived' ? true : undefined,
        spam: filter === 'spam' ? true : undefined,
      }),
  });

  const remove = useMutation({
    mutationFn: (uuid: string) =>
      api.delete<{ messages: number; files: number }>(`/sessions/${uuid}`),
    onError: (error) => {
      setNote(
        error instanceof ApiRequestError ? `Could not delete: ${error.message}` : 'Could not delete.',
      );
    },
    onSuccess: (result) => {
      setNote(
        `Deleted — ${String(result.messages)} message${result.messages === 1 ? '' : 's'}` +
          (result.files > 0 ? ` and ${String(result.files)} image${result.files === 1 ? '' : 's'}` : '') +
          '.',
      );
      // Every list of conversations, not just this one: the same rows are shown
      // in Chats and counted in its tab badges.
      void queryClient.invalidateQueries({ queryKey: ['management', 'sessions'] });
      refreshSessions(queryClient);
    },
  });

  return (
    <Section
      title="All conversations"
      description="Everything in the workspace, including archived and spam."
      actions={
        <Select<'all' | 'archived' | 'spam'>
          className="w-40"
          aria-label="Filter conversations"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'archived', label: 'Archived' },
            { value: 'spam', label: 'Spam' },
          ]}
        />
      }
    >
      {note ? (
        <p className="mb-4 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {note}
        </p>
      ) : null}

      {sessions.isLoading ? (
        <Spinner className="h-5 w-5 text-brand-600" />
      ) : (sessions.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="Nothing here" />
      ) : (
        <div className="lg-scroll -mx-5 overflow-x-auto">
          <table className="w-full min-w-[42rem] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <th className="px-5 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 font-medium">Channel</th>
                <th className="px-3 py-2 font-medium">Assigned</th>
                <th className="px-3 py-2 font-medium">Messages</th>
                <th className="px-5 py-2 font-medium">Updated</th>
                {isOwner ? <th className="px-5 py-2" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {(sessions.data?.items ?? []).map((session) => (
                <tr key={session.uuid}>
                  <td className="px-5 py-2.5">
                    <span className="block font-medium text-slate-800 dark:text-slate-100">
                      {session.customerName}
                    </span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400">
                      {session.customerEmail ?? session.customerPhone ?? '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <ChannelBadge type={session.type} />
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">
                    {session.assignedTo?.displayName ?? '—'}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-slate-600 dark:text-slate-300">
                    {session.messageCount}
                  </td>
                  <td className="px-5 py-2.5 text-slate-500 dark:text-slate-400">
                    {formatRelative(session.updatedAt)}
                  </td>

                  {isOwner ? (
                    <td className="px-5 py-2.5 text-right">
                      <button
                        type="button"
                        className="lg-btn-ghost px-2 hover:text-rose-600"
                        onClick={() => {
                          void confirm({
                            title: `Delete the conversation with ${session.customerName}?`,
                            message: `Its ${String(session.messageCount)} message${session.messageCount === 1 ? '' : 's'} and any images in them are destroyed. This cannot be undone, and the customer keeps their own copy of whatever was sent by SMS or email.`,
                            confirmLabel: 'Delete',
                            destructive: true,
                          }).then((ok) => {
                            if (ok) remove.mutate(session.uuid);
                          });
                        }}
                      >
                        <TrashIcon className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function TemplatesTab(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Partial<Record<TemplateType, string>>>({});

  const templates = useQuery({
    queryKey: ['management', 'templates'],
    queryFn: () => api.get<{ items: { type: TemplateType; title: string; body: string }[] }>('/management/templates'),
  });

  const save = useMutation({
    mutationFn: (input: { type: TemplateType; body: string }) => api.put('/management/templates', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['management', 'templates'] });
    },
  });

  if (templates.isLoading) return <Spinner className="h-5 w-5 text-brand-600" />;

  const byType = new Map((templates.data?.items ?? []).map((item) => [item.type, item.body]));

  return (
    <>
      {TEMPLATE_TYPES.map((type) => {
        const value = drafts[type] ?? byType.get(type) ?? '';

        return (
          <Section
            key={type}
            title={TEMPLATE_LABELS[type]}
            description={
              type === 'welcome'
                ? 'Sent the moment a customer opens a new conversation.'
                : type === 'no_agents_online'
                  ? 'Sent to a widget visitor who writes in while nobody is connected.'
                  : 'Sent when matching stock arrives. Use {{item}} for its name, plus {{customerName}} and the key of any field you declared on the Inventory page.'
            }
          >
            <textarea
              className="lg-input min-h-24 resize-y"
              value={value}
              onChange={(event) => {
                setDrafts((previous) => ({ ...previous, [type]: event.target.value }));
              }}
            />
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                className="lg-btn-primary"
                disabled={save.isPending || value.trim().length === 0}
                onClick={() => {
                  save.mutate({ type, body: value });
                }}
              >
                {save.isPending ? <Spinner /> : null}
                Save
              </button>
            </div>
          </Section>
        );
      })}
    </>
  );
}

interface EngagementRow {
  id: number;
  status: string;
  type: string;
  title: string;
  markup: unknown;
}

function EngagementsTab(): React.JSX.Element {
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const engagements = useQuery({
    queryKey: ['management', 'engagements'],
    queryFn: () => api.get<{ items: EngagementRow[] }>('/management/engagements'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/management/engagements/${String(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['management', 'engagements'] });
    },
  });

  return (
    <Section
      title="Proactive engagements"
      description="Pop-ups the widget shows when its conditions are met."
    >
      {engagements.isLoading ? (
        <Spinner className="h-5 w-5 text-brand-600" />
      ) : (engagements.data?.items.length ?? 0) === 0 ? (
        <EmptyState
          title="No engagements yet"
          description="Create one to invite visitors into a conversation before they leave."
        />
      ) : (
        <ul className="divide-y divide-slate-200 dark:divide-slate-800">
          {(engagements.data?.items ?? []).map((item) => (
            <li key={item.id} className="flex items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-50">
                  {item.title}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{item.type}</p>
              </div>

              <span
                className={
                  item.status === 'enabled'
                    ? 'lg-badge bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200'
                    : 'lg-badge bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                }
              >
                {item.status}
              </span>

              <button
                type="button"
                className="lg-btn-ghost px-2 hover:text-rose-600"
                onClick={() => {
                  void confirm({
                    title: `Delete “${item.title}”?`,
                    message: 'The widget stops showing this prompt to visitors.',
                    confirmLabel: 'Delete',
                    destructive: true,
                  }).then((ok) => {
                    if (ok) remove.mutate(item.id);
                  });
                }}
              >
                <TrashIcon className="h-4 w-4" />
                <span className="sr-only">Delete</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

interface FormRow {
  id: number;
  uuid: string;
  title: string;
  url: string;
  index: number;
  captureOtherPages: boolean;
  captureFormsWithSameFields: boolean;
  source: unknown;
  mapFields: Record<string, unknown>;
  /**
   * Carried through an edit untouched. Neither has an editor yet, and the API
   * replaces the whole row, so dropping them from the payload would quietly
   * erase the conditions and the SMS notification of every form imported from
   * WordPress the first time somebody renamed one.
   */
  conditions: unknown;
  meta: unknown;
}

interface CapturedRow {
  id: number;
  createdAt: string;
  /** Page the form was submitted from; null on rows captured before it was sent. */
  pageUrl: string | null;
  formTitle: string | null;
  /** Resolved through the form's own mapping — the raw field names are the site's. */
  name: string | null;
  email: string | null;
  phone: string | null;
  fields: Record<string, string>;
  processed: boolean;
  result: string | null;
}

/**
 * The part of a captured page URL worth a column.
 *
 * Every submission in a workspace comes off the same site, so the scheme and
 * host are the same on every row and the only thing that distinguishes them —
 * the path — is what gets truncated away. `https://monste…` five times over
 * says nothing; `/request-part/` says which form it was. The full URL is still
 * the link and the tooltip.
 */
function shortUrl(raw: string): string {
  try {
    const url = new URL(raw);
    const path = `${url.pathname}${url.search}`;

    return path === '/' || path === '' ? url.host : path;
  } catch {
    return raw;
  }
}

/**
 * What the capture worker made of a submission.
 *
 * Worth showing, because "captured" and "reached somebody" are not the same
 * thing: a submission that matched no tracked form is emailed to the team and
 * nothing more, and without this the owner has no way to tell that apart from
 * one that opened a conversation.
 */
function readCaptureOutcome(
  raw: string | null,
): { label: string; title: string; className: string; detail?: string; sessionUuid?: string } | null {
  if (!raw) return null;

  let parsed: { status?: string; reason?: string; sessionUuid?: string };

  try {
    parsed = JSON.parse(raw) as typeof parsed;
  } catch {
    return null;
  }

  // One word in the row, the sentence on hover and in the dialog. The badge
  // shares its column with the timestamp, and a label that wraps to three lines
  // pushes every row apart for no gain.
  const shown: Record<string, { label: string; title: string; className: string }> = {
    created: {
      label: 'Opened',
      title: 'Opened a conversation',
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200',
    },
    appended: {
      label: 'Added',
      title: 'Added to an existing conversation',
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200',
    },
    duplicate: {
      label: 'Duplicate',
      title: 'The same submission was already in the conversation',
      className: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    },
    unmatched: {
      label: 'Emailed',
      title: 'Matched no tracked form — emailed to the team and nothing more',
      className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200',
    },
    incomplete: {
      label: 'Emailed',
      title: 'Carried no email or phone — emailed to the team and nothing more',
      className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200',
    },
    error: {
      label: 'Failed',
      title: 'The capture could not be converted',
      className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-200',
    },
  };

  const style = shown[parsed.status ?? ''];
  if (!style) return null;

  return {
    ...style,
    // The happy paths speak for themselves; the rest need to say what went wrong.
    detail: parsed.status === 'created' || parsed.status === 'appended' ? undefined : parsed.reason,
    sessionUuid: parsed.sessionUuid,
  };
}

/**
 * One submission in full: every field under the name the site gave it, and what
 * the capture worker did with it.
 *
 * The table resolves three fields through the form's mapping and shows nothing
 * else, which is right for scanning a list and wrong for answering "what did
 * they actually say" — a message, a budget, a car they asked about all live in
 * the fields nobody mapped.
 */
function CapturedDetail({ item }: { item: CapturedRow }): React.JSX.Element {
  const outcome = readCaptureOutcome(item.result);
  const entries = Object.entries(item.fields);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <span>{new Date(item.createdAt).toLocaleString()}</span>
        {outcome ? (
          <span className={clsx('lg-badge', outcome.className)}>{outcome.label}</span>
        ) : null}
        {outcome?.detail ? <span>{outcome.detail}</span> : null}
      </div>

      {item.pageUrl ? (
        <p className="break-all text-xs">
          <a
            href={item.pageUrl}
            target="_blank"
            rel="noreferrer"
            className="text-brand-600 hover:underline dark:text-brand-300"
          >
            {item.pageUrl}
          </a>
        </p>
      ) : null}

      {entries.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          This submission arrived with no fields in it.
        </p>
      ) : (
        <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[minmax(6rem,auto)_1fr]">
          {entries.map(([name, value]) => (
            <div key={name} className="contents">
              <dt className="break-words text-slate-500 dark:text-slate-400">{name}</dt>
              <dd className="whitespace-pre-wrap break-words text-slate-800 dark:text-slate-100">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {outcome?.sessionUuid ? (
        <Link
          to={`/chats/${outcome.sessionUuid}`}
          className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-300"
        >
          Open the conversation
        </Link>
      ) : null}
    </div>
  );
}

function FormsTab(): React.JSX.Element {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');
  const [scanned, setScanned] = useState<{ id: string | null; fields: { name: string }[] }[] | null>(null);
  /**
   * The form the dialog is currently about — a freshly scanned one when `id` is
   * null, an already tracked one otherwise. `source`, `mapFields`, `conditions`
   * and `meta` are the row as stored, held here so an edit writes back
   * everything it does not manage rather than replacing it with defaults.
   */
  const [tracking, setTracking] = useState<{
    id: number | null;
    index: number;
    fields: string[];
    title: string;
    url: string;
    map: Record<TrackedFormLabel, string>;
    captureOtherPages: boolean;
    captureFormsWithSameFields: boolean;
    source: unknown;
    mapFields: Record<string, unknown>;
    conditions: unknown;
    meta: unknown;
  } | null>(null);

  const [error, setError] = useState<string | null>(null);

  const forms = useQuery({
    queryKey: ['management', 'forms'],
    queryFn: () => api.get<{ items: FormRow[] }>('/management/forms'),
  });

  const scan = useMutation({
    mutationFn: () =>
      api.post<{ forms: { id: string | null; fields: { name: string }[] }[] }>(
        '/management/forms/scan',
        { url },
      ),
    onSuccess: (result) => {
      setScanned(result.forms);
      setError(null);
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not scan that page');
      setScanned(null);
    },
  });

  const captured = useQuery({
    queryKey: ['management', 'forms', 'captured'],
    queryFn: () => api.get<{ items: CapturedRow[] }>('/management/forms/captured'),
  });

  /** The submission whose full field list is open. */
  const [viewing, setViewing] = useState<CapturedRow | null>(null);

  const forgetCapture = useMutation({
    mutationFn: (id: number | 'all') =>
      api.delete(id === 'all' ? '/management/forms/captured' : `/management/forms/captured/${String(id)}`),
    onSuccess: () => {
      setViewing(null);
      void queryClient.invalidateQueries({ queryKey: ['management', 'forms', 'captured'] });
    },
  });

  const track = useMutation({
    mutationFn: () => {
      if (!tracking) throw new Error('Nothing to track');

      const body = {
        title: tracking.title,
        url: tracking.url,
        index: tracking.index,
        captureOtherPages: tracking.captureOtherPages,
        captureFormsWithSameFields: tracking.captureFormsWithSameFields,
        // The stored shape verbatim on an edit; a new form has none yet, so the
        // scanned names become it.
        source: tracking.source ?? tracking.fields,
        mapFields: writeTrackedFormMapping(tracking.mapFields, tracking.map),
        conditions: tracking.conditions ?? [],
        meta: tracking.meta ?? {},
      };

      return tracking.id === null
        ? api.post('/management/forms', body)
        : api.patch(`/management/forms/${String(tracking.id)}`, body);
    },
    onSuccess: () => {
      setTracking(null);
      void queryClient.invalidateQueries({ queryKey: ['management', 'forms'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/management/forms/${String(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['management', 'forms'] });
    },
  });

  return (
    <>
      <Section
        title="Scan a page for forms"
        description="Point at a page on your site and we will list the forms we can capture."
      >
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            scan.mutate();
          }}
        >
          <div className="min-w-[18rem] flex-1">
            <label className="lg-label" htmlFor="scan-url">
              Page URL
            </label>
            <input
              id="scan-url"
              type="url"
              required
              placeholder="https://example.com/contact"
              className="lg-input"
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
              }}
            />
          </div>
          <button type="submit" className="lg-btn-primary" disabled={scan.isPending}>
            {scan.isPending ? <Spinner /> : null}
            Scan
          </button>
        </form>

        {error ? (
          <div className="mt-3">
            <ErrorNotice message={error} />
          </div>
        ) : null}

        {scanned ? (
          scanned.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              No forms with named, visible fields were found on that page.
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {scanned.map((form, index) => (
                <li
                  key={form.id ?? index}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
                >
                  <span className="font-medium text-slate-800 dark:text-slate-100">
                    Form {index + 1}
                    {form.id ? ` (#${form.id})` : ''}
                  </span>
                  <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                    {form.fields.map((field) => field.name).join(', ')}
                  </span>

                  <button
                    type="button"
                    className="lg-btn-secondary mt-2"
                    onClick={() => {
                      const names = form.fields.map((field) => field.name);
                      const guess = (...hints: string[]) =>
                        names.find((name) =>
                          hints.some((hint) => name.toLowerCase().includes(hint)),
                        ) ?? '';

                      setTracking({
                        id: null,
                        index,
                        fields: names,
                        title: `Form ${String(index + 1)} on ${url}`,
                        url,
                        // Guessed from the field names, because "your-email"
                        // is an email on almost every site that has one.
                        map: {
                          Name: guess('name'),
                          Email: guess('email', 'mail'),
                          Phone: guess('phone', 'tel'),
                        },
                        captureOtherPages: false,
                        captureFormsWithSameFields: false,
                        source: null,
                        mapFields: {},
                        conditions: null,
                        meta: null,
                      });
                    }}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Track this form
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : null}
      </Section>

      <Section title="Tracked forms">
        {forms.isLoading ? (
          <Spinner className="h-5 w-5 text-brand-600" />
        ) : (forms.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No tracked forms"
            description="Scan a page above, then add the form you want captured."
          />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {(forms.data?.items ?? []).map((form) => (
              <li key={form.id} className="flex items-center gap-1 py-3">
                <div className="min-w-0 flex-1 pr-2">
                  <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-50">
                    {form.title}
                  </p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">{form.url}</p>
                </div>
                <button
                  type="button"
                  className="lg-btn-ghost px-2"
                  onClick={() => {
                    const names = trackedFormFieldNames(form.source, form.index);

                    setTracking({
                      id: form.id,
                      index: form.index,
                      // What the form was configured against, not what the page
                      // has now. Re-scanning is how you pick up a changed form;
                      // opening this dialog must not silently alter what the
                      // capture matches on.
                      fields: names,
                      title: form.title,
                      url: form.url,
                      map: readTrackedFormMapping(form.mapFields),
                      captureOtherPages: form.captureOtherPages,
                      captureFormsWithSameFields: form.captureFormsWithSameFields,
                      source: form.source,
                      mapFields: form.mapFields,
                      conditions: form.conditions,
                      meta: form.meta,
                    });
                  }}
                >
                  <PencilIcon className="h-4 w-4" />
                  <span className="sr-only">Edit</span>
                </button>
                <button
                  type="button"
                  className="lg-btn-ghost px-2 hover:text-rose-600"
                  onClick={() => {
                    void confirm({
                      title: `Delete “${form.title}”?`,
                      message: 'Submissions already captured are kept; new ones stop arriving.',
                      confirmLabel: 'Delete',
                      destructive: true,
                    }).then((ok) => {
                      if (ok) remove.mutate(form.id);
                    });
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                  <span className="sr-only">Delete</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Captured submissions"
        description="What people actually filled in, and what became of it."
        actions={
          (captured.data?.items.length ?? 0) > 0 ? (
            <button
              type="button"
              className="lg-btn-ghost hover:text-rose-600"
              onClick={() => {
                void confirm({
                  title: 'Clear every captured submission?',
                  message:
                    'The log is destroyed and cannot be recovered. Conversations opened from these submissions are kept — this only throws away the record of the form itself.',
                  confirmLabel: 'Clear the log',
                  destructive: true,
                }).then((ok) => {
                  if (ok) forgetCapture.mutate('all');
                });
              }}
            >
              <TrashIcon className="h-4 w-4" />
              Clear all
            </button>
          ) : null
        }
      >
        {captured.isLoading ? (
          <Spinner className="h-5 w-5 text-brand-600" />
        ) : (captured.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="Nothing captured yet"
            description="Submissions appear here once someone fills in a form you track."
          />
        ) : (
          // `table-fixed` with declared widths, so the columns cannot be pushed
          // wider than the card by a long URL or an address nobody shortened —
          // the cells truncate instead, and the page never scrolls sideways.
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                {/* `formatRelative` is terse — "3h", "2d", "14 Sep" — so the
                    badge underneath is what this column has to fit. */}
                <th className="w-24 py-2 pr-3 font-medium">Created</th>
                <th className="w-32 px-3 py-2 font-medium">Name</th>
                {/* Contact takes what is left: an email is the one cell here
                    worth reading in full, and the widest. */}
                <th className="px-3 py-2 font-medium">Contact</th>
                <th className="hidden w-28 px-3 py-2 font-medium sm:table-cell">Page</th>
                <th className="w-16 py-2 pl-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {(captured.data?.items ?? []).map((item) => {
                const outcome = readCaptureOutcome(item.result);

                return (
                  <tr key={item.id}>
                    <td className="py-2.5 pr-3 align-top">
                      <span className="block truncate text-slate-600 dark:text-slate-300">
                        {formatRelative(item.createdAt)}
                      </span>
                      {outcome ? (
                        <span
                          className={clsx('lg-badge mt-1', outcome.className)}
                          title={outcome.detail ?? outcome.title}
                        >
                          {outcome.label}
                        </span>
                      ) : null}
                    </td>
                    <td
                      className="truncate px-3 py-2.5 align-top font-medium text-slate-800 dark:text-slate-100"
                      title={item.name ?? undefined}
                    >
                      {item.name ?? '—'}
                    </td>
                    <td className="px-3 py-2.5 align-top text-slate-600 dark:text-slate-300">
                      {item.email === null && item.phone === null ? (
                        '—'
                      ) : (
                        <>
                          {item.email ? (
                            <a
                              href={`mailto:${item.email}`}
                              className="block truncate hover:underline"
                              title={item.email}
                            >
                              {item.email}
                            </a>
                          ) : null}
                          {item.phone ? (
                            <span className="block truncate text-slate-500 dark:text-slate-400">
                              {item.phone}
                            </span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className="hidden px-3 py-2.5 align-top sm:table-cell">
                      {item.pageUrl ? (
                        <a
                          href={item.pageUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="block truncate text-brand-600 hover:underline dark:text-brand-300"
                          title={item.pageUrl}
                        >
                          {shortUrl(item.pageUrl)}
                        </a>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500">—</span>
                      )}
                    </td>
                    <td className="py-2.5 pl-3 align-top">
                      <div className="flex items-center justify-end">
                        <button
                          type="button"
                          className="lg-btn-ghost px-1.5"
                          onClick={() => {
                            setViewing(item);
                          }}
                        >
                          <EyeIcon className="h-4 w-4" />
                          <span className="sr-only">View the submission</span>
                        </button>
                        <button
                          type="button"
                          className="lg-btn-ghost px-1.5 hover:text-rose-600"
                          onClick={() => {
                            void confirm({
                              title: 'Delete this submission?',
                              message:
                                'Only the captured record goes. Any conversation it opened is kept.',
                              confirmLabel: 'Delete',
                              destructive: true,
                            }).then((ok) => {
                              if (ok) forgetCapture.mutate(item.id);
                            });
                          }}
                        >
                          <TrashIcon className="h-4 w-4" />
                          <span className="sr-only">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {(captured.data?.items.length ?? 0) >= 100 ? (
          // The endpoint caps the list. Saying so beats letting an owner
          // conclude their older submissions were thrown away.
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Showing the 100 most recent. Clearing the log removes older ones too.
          </p>
        ) : null}
      </Section>

      <Modal
        open={viewing !== null}
        title="Captured submission"
        description={viewing?.formTitle ?? undefined}
        onClose={() => {
          setViewing(null);
        }}
        footer={
          <button
            type="button"
            className="lg-btn-secondary"
            onClick={() => {
              setViewing(null);
            }}
          >
            Close
          </button>
        }
      >
        {viewing ? <CapturedDetail item={viewing} /> : null}
      </Modal>

      <Modal
        open={tracking !== null}
        title={tracking?.id === null ? 'Track this form' : 'Edit this form'}
        description="Every submission is captured and turned into a conversation. Saying which field is which is what gives that conversation a name, an address and a phone number."
        onClose={() => {
          setTracking(null);
          track.reset();
        }}
        footer={
          <>
            <button
              type="button"
              className="lg-btn-secondary"
              onClick={() => {
                setTracking(null);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="track-form"
              className="lg-btn-primary"
              disabled={track.isPending || !tracking?.title.trim() || !tracking.url.trim()}
            >
              {track.isPending ? <Spinner /> : null}
              {tracking?.id === null ? 'Track this form' : 'Save changes'}
            </button>
          </>
        }
      >
        {tracking ? (
          <form
            id="track-form"
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              track.mutate();
            }}
          >
            {track.error ? (
              <p
                className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/50 dark:text-rose-200"
                role="alert"
              >
                {track.error instanceof ApiRequestError
                  ? track.error.message
                  : 'Could not save this form.'}
              </p>
            ) : null}

            <div>
              <label className="lg-label" htmlFor="track-title">
                Name it
              </label>
              <input
                id="track-title"
                className="lg-input"
                value={tracking.title}
                onChange={(event) => {
                  setTracking({ ...tracking, title: event.target.value });
                }}
              />
            </div>

            <div>
              <label className="lg-label" htmlFor="track-url">
                Page it lives on
              </label>
              <input
                id="track-url"
                className="lg-input"
                type="url"
                value={tracking.url}
                onChange={(event) => {
                  setTracking({ ...tracking, url: event.target.value });
                }}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                The widget only watches this page, unless you tick the box below.
              </p>
            </div>

            <fieldset className="grid gap-3">
              <legend className="lg-label">Which field holds what</legend>

              {tracking.fields.length === 0 ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  This form was saved without its field list, so there is nothing to choose from.
                  Scan the page again and track it afresh to fix that.
                </p>
              ) : null}

              {TRACKED_FORM_LABELS.map((label) => (
                <div key={label} className="grid gap-2 sm:grid-cols-[6rem_1fr] sm:items-center">
                  <span className="text-sm text-slate-600 dark:text-slate-300">{label}</span>
                  <Select
                    value={tracking.map[label]}
                    options={[
                      { value: '', label: '— this form has none' },
                      ...tracking.fields.map((field) => ({ value: field, label: field })),
                    ]}
                    onChange={(field) => {
                      setTracking({ ...tracking, map: { ...tracking.map, [label]: field } });
                    }}
                  />
                </div>
              ))}
            </fieldset>

            <label className="flex cursor-pointer items-start gap-2.5 text-sm">
              <input
                type="checkbox"
                className="lg-checkbox mt-0.5"
                checked={tracking.captureOtherPages}
                onChange={(event) => {
                  setTracking({ ...tracking, captureOtherPages: event.target.checked });
                }}
              />
              <span>
                <span className="font-medium text-slate-800 dark:text-slate-100">
                  Capture it on other pages too
                </span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  For a form that appears site-wide, like one in the footer.
                </span>
              </span>
            </label>

            <label className="flex cursor-pointer items-start gap-2.5 text-sm">
              <input
                type="checkbox"
                className="lg-checkbox mt-0.5"
                checked={tracking.captureFormsWithSameFields}
                onChange={(event) => {
                  setTracking({ ...tracking, captureFormsWithSameFields: event.target.checked });
                }}
              />
              <span>
                <span className="font-medium text-slate-800 dark:text-slate-100">
                  Capture any form with these same fields
                </span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  Useful when the same form is rendered with a different id on each page.
                </span>
              </span>
            </label>
          </form>
        ) : null}
      </Modal>
    </>
  );
}
